import { useEffect, useId, useRef, useState } from 'react'
import { Icon } from './Icon'
import { cx } from '@/lib/cx'

interface Props {
  label: string
  value: string
  /** Resolves when it is stored. Rejecting puts the old value back and the
   *  reason on screen — a save that silently did nothing is worse than none. */
  onSave: (next: string) => Promise<unknown>
  placeholder?: string
  type?: 'text' | 'email'
  /** Refuse to save an empty value, for the fields that must have one. */
  required?: boolean
  autoCapitalize?: 'none' | 'words'
}

type State = 'idle' | 'saving' | 'saved' | 'failed'

/**
 * A value that reads as text until you go to change it.
 *
 * It is always a real `<input>` — swapping a `<span>` for a field on click
 * looks the same and loses the label, the tab stop and every affordance a
 * screen reader has. Only the border is conditional: nothing at rest, an outline
 * on hover to say it can be changed, a full field once it has focus.
 *
 * Saving happens on blur rather than behind a button. There is no draft worth
 * protecting here — it is one short value, and the alternative is a Save button
 * per row or one that quietly applies four rows at once.
 */
export function InlineEdit({
  label,
  value,
  onSave,
  placeholder,
  type = 'text',
  required = false,
  autoCapitalize,
}: Props) {
  const id = useId()
  const [text, setText] = useState(value)
  const [state, setState] = useState<State>('idle')
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)
  // Escape resets the text and blurs, but the reset has not reached `text` by
  // the time blur fires — so the save has to be waved off explicitly.
  const abandoned = useRef(false)

  // The value can change under us — another save, a refetch.
  useEffect(() => {
    setText(value)
  }, [value])

  async function commit() {
    if (abandoned.current) {
      abandoned.current = false
      return
    }
    const next = text.trim()
    if (next === value.trim()) {
      setState('idle')
      setError(null)
      return
    }
    if (required && !next) {
      setText(value)
      setError(`${label} cannot be empty.`)
      setState('failed')
      return
    }

    setState('saving')
    setError(null)
    try {
      await onSave(next)
      setState('saved')
      // Long enough to be seen, short enough not to become furniture.
      setTimeout(() => setState((s) => (s === 'saved' ? 'idle' : s)), 2000)
    } catch (caught) {
      // Put back what was there. Leaving the rejected text in the box implies
      // it was kept.
      setText(value)
      setError(caught instanceof Error ? caught.message : 'Could not save that.')
      setState('failed')
    }
  }

  return (
    <div className="w-full sm:w-72">
      <div className="relative">
        <input
          ref={input}
          id={id}
          type={type}
          aria-label={label}
          autoComplete="off"
          {...(autoCapitalize ? { autoCapitalize } : {})}
          placeholder={placeholder}
          value={text}
          disabled={state === 'saving'}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              input.current?.blur()
            }
            if (event.key === 'Escape') {
              event.preventDefault()
              abandoned.current = true
              setText(value)
              setError(null)
              setState('idle')
              input.current?.blur()
            }
          }}
          className={cx(
            'min-h-field w-full rounded-md border bg-transparent px-3 py-2 text-base leading-snug text-fg transition-colors duration-150 ease-standard',
            'placeholder:text-fg-subtle sm:text-right sm:focus:text-left',
            // `not-disabled` on the focus rule too, or the hover rule wins: the
            // extra `:not(:disabled)` outranks a plain `focus:`, so a field that
            // is hovered AND focused kept the hover border.
            'focus:not-disabled:border-accent focus:bg-surface focus:ring-3 focus:ring-focus/20 focus:outline-none',
            'disabled:text-fg-muted',
            error
              ? 'border-danger'
              : 'border-transparent hover:not-disabled:border-control hover:not-disabled:bg-surface',
          )}
        />

        {/* Inside the field so the row never changes height as it saves. */}
        <span
          className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-success"
          aria-hidden="true"
        >
          {state === 'saved' && <Icon name="check" size="1rem" />}
        </span>
      </div>

      {/* One live region for both, so a screen reader hears the outcome
          whichever it was. */}
      <p role="status" aria-live="polite" className="sr-only">
        {state === 'saving' ? `Saving ${label}` : state === 'saved' ? `${label} saved` : ''}
      </p>
      {error && (
        <p className="mt-1 flex items-start gap-2 text-xs leading-snug text-danger" role="alert">
          <Icon name="alert" size="1rem" className="mt-[0.1em] flex-none" />
          {error}
        </p>
      )}
    </div>
  )
}
